package world.phantasmal.web.questEditor.models

import world.phantasmal.cell.Cell
import world.phantasmal.cell.map
import world.phantasmal.cell.mutableCell
import world.phantasmal.psolib.asm.dataFlowAnalysis.ScriptNpcSpawn
import world.phantasmal.psolib.fileFormats.quest.NpcType
import world.phantasmal.psolib.fileFormats.quest.QuestNpc
import world.phantasmal.web.externals.three.Vector3

class QuestNpcModel(
    npc: QuestNpc,
    waveId: Int,
    internal val placementPolicy: NpcPlacementPolicy,
    /** Present for a read-only preview derived from a quest VM creation opcode. */
    val scriptSpawn: ScriptNpcSpawn? = null,
) : QuestEntityModel<NpcType, QuestNpc>(npc) {
    val isScriptCreated: Boolean get() = scriptSpawn != null
    override val isEditable: Boolean = !isScriptCreated

    private val _waveId = mutableCell(waveId)

    private val _typeId = mutableCell(npc.typeId.toInt())
    private val _resolvedTypeRevision = mutableCell(0)

    /**
     * The raw type ID stored at offset 0 of the NPC data. Editing it changes which [NpcType] the
     * NPC resolves to. Exposed as a cell so the 3D mesh and the info panel can react to changes.
     */
    val typeId: Cell<Int> = _typeId
    val resolvedTypeRevision: Cell<Int> = _resolvedTypeRevision

    /**
     * Sets the raw type ID. The resolved [type] (and thus the model/properties) changes with it.
     */
    fun setTypeId(typeId: Int) {
        entity.typeId = typeId.toShort()
        _typeId.value = typeId
        refreshResolvedType()
    }

    internal fun refreshResolvedType() {
        rebuildPropertiesForCurrentType()
        _resolvedTypeRevision.value++
    }

    /**
     * Whether this NPC is a StageNPC (typeId=0x33).
     *
     * StageNPC is a single NPC type that maps to many different monsters via subtype (character ID).
     * Its Y position in quest data differs from regular NPCs of the same monster type (e.g.,
     * StageNPC GiGue y=-15 vs regular GiGue y=0~3.8), because the game engine handles StageNPC
     * positioning differently. We ignore the data Y and use section ground height + yOffset instead.
     */
    private val isStageNpc: Boolean get() = entity.typeId.toInt() == 0x33

    /**
     * Y offset for the currently resolved NPC type. For StageNPC, the type is resolved from the
     * NPC51 table via effective map and subtype/character ID.
     */
    private val yOffset: Double get() = computeYOffset(type)

    private val displayRevision: Cell<Unit> =
        map(resolvedTypeRevision, placementPolicy.groundHeightRevision) { _, _ -> }

    val wave: Cell<WaveModel> = map(_waveId, sectionId) { id, sectionId ->
        WaveModel(id, floorId, sectionId)
    }

    fun setWaveId(waveId: Int) {
        entity.wave = waveId.toShort()
        entity.wave2 = waveId
        _waveId.value = waveId
    }

    override val worldPosition: Cell<Vector3> =
        map(
            super.worldPosition,
            placementPolicy.spawnOnGround,
            section,
            displayRevision,
        ) { basePos, spawnOnGround, section, _ ->
            if (isStageNpc && section != null) {
                val groundY = calculateGroundHeight(basePos.x, basePos.z, section)
                Vector3(basePos.x, groundY + yOffset, basePos.z)
            } else if (spawnOnGround && section != null) {
                val groundY = calculateGroundHeight(basePos.x, basePos.z, section)
                Vector3(basePos.x, groundY + yOffset, basePos.z)
            } else if (yOffset != 0.0) {
                Vector3(basePos.x, basePos.y + yOffset, basePos.z)
            } else {
                basePos
            }
        }

    private fun calculateGroundHeight(x: Double, z: Double, section: SectionModel): Double =
        placementPolicy.groundHeight(x, z, section)

    companion object {
        /**
         * Y offset to prevent models from clipping into the ground when spawned at terrain height.
         * Values determined empirically by comparing editor placement with in-game appearance.
         */
        private fun computeYOffset(npcType: NpcType): Double = when (npcType) {
            NpcType.Epsilon, NpcType.EpsilonOmni -> 20.0
            NpcType.GiGue -> 25.0
            NpcType.ChaosSorcerer, NpcType.ChaosSorcerer2 -> 25.0
            NpcType.Bulclaw -> 25.0
            NpcType.Recon -> 10.0
            NpcType.Claw -> 10.0
            else -> 0.0
        }
    }
}
